// MA-896 — the ONE bridge from the skill tree's mastery projection to the
// review picker's skill state (KID-JOURNEY-2026-10-08 D-KJ-3).
//
// The tree's "Almost mastered" → "Mastered" step needs a passed delayed
// review (spacedReview.evaluateDelayedReview, MA-295), and a delayed review
// is stamped only in a spaced_review slot (MathSession / ReadingSession).
// The review picker (progression.ts pickReviewSkill) used to admit only
// skills whose ENGINE band (deriveMasteryBand, all-time accuracy ≥ 0.85) is
// secure/fluent, and MA-298's due tier skipped a skill once its MA-297
// confirm was in — so a tree-secure skill could wait forever for the one
// review that makes it Mastered.
//
// This file does NOT compute mastery. It reads the tree's own projection
// (masteryProjection.computeSkillMasteryProjection — the same function
// useSkillGraph's nodes and the recap use), bands its score with the tree's
// own masteryTier + SECURED_TREE_STATUSES, and reads the projection's own
// delayedReviewEvidence.passed. No threshold, no second mastery formula.
//
// MA-917 builds on this: "the focal joins the due tier" reuses
// isTreeReviewPending on the focal's state and progression.ts's
// isDueForReviewSlot — no second formula there either.

import type { ChildAttempt } from "@/curriculum/types/adaptationTypes";
import type { SkillState } from "@/types/progression";
import { computeSkillMasteryProjection } from "./masteryProjection";
import { masteryTier } from "./masteryTiers";
import { SECURED_TREE_STATUSES } from "./skillTreeStatus";

/** The picker's skill state with the bridge's one optional stamp. Set ONLY
 *  by aggregateSkillStatesFromAttempts (via treeReviewPending) and only when
 *  true, so a state with no pending tree review is byte-identical to the
 *  pre-MA-896 SkillState. */
export type ReviewBridgedSkillState = SkillState & { treeReviewPending?: true };

/** The bridge: true iff the skill tree renders this skill as secured
 *  (masteryTier(projection score) ∈ SECURED_TREE_STATUSES) AND its delayed
 *  review has not passed (projection.delayedReviewEvidence.passed false).
 *  `skillAttempts` = this skill's attempts, oldest → newest (the order the
 *  tree's merged rows and the aggregator both use). Pure. */
export function treeReviewPending(
  skillId: string,
  skillAttempts: readonly ChildAttempt[],
): boolean {
  if (skillAttempts.length === 0) return false;
  // The aggregator is total over partial rows (legacy and local-mirror rows can
  // lack supportMode, test:local-attempt-history); the tree's projection is
  // not. A row set the projection cannot read is not a tree candidate
  // (fail-closed: the pre-MA-896 pick). The explicit check states the
  // contract; the catch is only a backstop for any other unreadable row.
  if (!skillAttempts.every((a) => Array.isArray(a.supportMode))) return false;
  let projection: ReturnType<typeof computeSkillMasteryProjection>;
  try {
    projection = computeSkillMasteryProjection(skillId, skillAttempts);
  } catch {
    return false;
  }
  const treeSecured = (SECURED_TREE_STATUSES as readonly string[]).includes(
    masteryTier(projection.score),
  );
  return treeSecured && projection.delayedReviewEvidence.passed !== true;
}

/** Reader for the picker: has the aggregator stamped this state as a
 *  tree-secure skill whose delayed review has not passed? Absent ⇒ false
 *  (synthetic states, withAgeImpliedMastery, pure unit callers). */
export function isTreeReviewPending(state: SkillState): boolean {
  return (state as ReviewBridgedSkillState).treeReviewPending === true;
}
